import { z } from "zod";
import { predictionFormSchema } from "@/features/prediction/model/form";
import type { PredictionRequest } from "@/features/prediction/model/types";
import {
  PLATFORM_IDS,
} from "@/features/prediction/model/types";
import type {
  PredictionHistoryRecord,
  PredictionHistorySummary,
} from "@/features/prediction/model/predictionHistory";
import type { JsonValue } from "@/lib/json";
import { apiFetch } from "@/lib/api/client";

const HISTORY_PATH = "/api/v2/history";

const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);

const kpisSchema = z.record(z.number().nullable()).transform(values =>
  Object.fromEntries(Object.entries(values).filter((entry): entry is [string, number] => entry[1] !== null)),
);
const platformBudgetsSchema = z.object({
  douyin: z.number().nonnegative(),
  xhs: z.number().nonnegative(),
  wechat_video: z.number().nonnegative(),
  bilibili: z.number().nonnegative(),
  kuaishou: z.number().nonnegative(),
  tiktok: z.number().nonnegative(),
  instagram: z.number().nonnegative(),
  youtube: z.number().nonnegative(),
});

const predictionRequestSchema: z.ZodType<PredictionRequest> = z.object({
  creative: z.object({
    caption: z.string(),
    duration_sec: z.number(),
    visual_style: z.string(),
    music_mood: z.string(),
    has_celeb: z.boolean(),
  }),
  project_name: z.string().optional(),
  campaign_objective: z.string().optional(),
  core_selling_points: z.array(z.string()).optional(),
  source: z.enum(["llm", "mock"]).nullable().optional(),
  platform_budgets: platformBudgetsSchema.optional(),
  total_budget: z.number().nonnegative(),
  platform_alloc: z.record(z.number().nonnegative()),
  audience_age_buckets: z.array(z.number().int()).nullable().optional(),
  audience_gender: z
    .union([z.array(z.number().int()), z.number().int()])
    .nullable()
    .optional(),
  audience_city_tiers: z.array(z.number().int()).nullable().optional(),
  kol_niche: z.string().nullable().optional(),
  use_llm: z.boolean(),
  llm_calibrate: z.boolean(),
  n_souls: z.number().int().positive(),
  lifecycle_days: z.number().int().positive(),
  today: z.string().nullable().optional(),
  daypart: z.string(),
  weather_temp_c: z.number(),
  rainy: z.boolean(),
  sentiment: z.string(),
  cross_platform_overlap: z.number().min(0).max(1),
  enable_crossplat: z.boolean(),
  enable_discourse: z.boolean(),
  discourse_n_comments: z.number().int().positive(),
  enable_brand_memory: z.boolean(),
  brand_memory_days: z.number().int().positive().optional(),
  enable_recsys_rl: z.boolean(),
  enable_groupchat: z.boolean(),
  groupchat_n_agents: z.number().int().positive(),
  groupchat_n_rounds: z.number().int().positive(),
  groupchat_stance_overrides: z.record(z.number()).nullable().optional(),
  own_brand: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  competitors: z.array(z.string()).nullable().optional(),
  target_niches: z.array(z.string()).nullable().optional(),
  enable_kol_ilp: z.boolean(),
  enable_search_elasticity: z.boolean(),
  season: z.string().nullable().optional(),
  holiday_context: z.string().nullable().optional(),
  upcoming_holiday: z.string().nullable().optional(),
  weather_desc: z.string().nullable().optional(),
  sentiment_reason: z.string().nullable().optional(),
  world_news_summary: z.array(z.string()).nullable().optional(),
  competitor_moves: z.array(z.string()).nullable().optional(),
  social_trends: z.array(z.string()).nullable().optional(),
  policy_regulation: z.array(z.string()).nullable().optional(),
});

const v2RequestSchema = z.object({
  caption: z.string().min(1),
  project_name: z.string(),
  campaign_objective: z.string(),
  own_brand: z.string(),
  category: z.string(),
  core_selling_points: z.array(z.string()),
  competitors: z.array(z.string()),
  source: z.enum(["llm", "mock"]).nullable(),
  duration_sec: z.number().positive(),
  visual_style: z.string(),
  music_mood: z.string(),
  has_celeb: z.boolean(),
  platform_budgets: platformBudgetsSchema,
  audience_age_bucket: z.array(z.number().int()).nullable(),
  audience_gender: z.array(z.number().int()).nullable(),
  audience_city_tier: z.array(z.number().int()).nullable(),
  lifecycle_days: z.number().int().positive(),
  use_llm: z.boolean(),
  llm_calibrate: z.boolean(),
  n_souls: z.number().int().positive(),
  enable_crossplat: z.boolean(),
  enable_discourse: z.boolean(),
  discourse_n_comments: z.number().int().positive(),
  enable_groupchat: z.boolean(),
  groupchat_n_agents: z.number().int().positive(),
  groupchat_n_rounds: z.number().int().positive(),
  enable_brand_memory: z.boolean(),
  brand_memory_days: z.number().int().positive(),
  enable_recsys_rl: z.boolean(),
  enable_kol_ilp: z.boolean(),
  enable_search_elasticity: z.boolean(),
  today: z.string().nullable(),
  daypart: z.string(),
  weather_temp_c: z.number(),
  weather_desc: z.string().nullable(),
  rainy: z.boolean(),
  season: z.string().nullable(),
  holiday_context: z.string().nullable(),
  upcoming_holiday: z.string().nullable(),
  sentiment: z.string(),
  sentiment_reason: z.string().nullable(),
  world_news_summary: z.array(z.string()).nullable(),
  competitor_moves: z.array(z.string()).nullable(),
  social_trends: z.array(z.string()).nullable(),
  policy_regulation: z.array(z.string()).nullable(),
  cross_platform_overlap: z.number().min(0).max(1),
});

const historySummarySchema = z.object({
  id: z.string().min(1),
  created_at: z.number().nonnegative(),
  elapsed_ms: z.number().int().nonnegative(),
  status: z.enum(["running", "completed", "failed"]).default("completed"),
  current_step: z.string().nullable().default(null),
  error_message: z.string().nullable().default(null),
  request: z.union([predictionRequestSchema, v2RequestSchema]),
  kpis: kpisSchema,
});

const predictionPayloadSchema = z
  .object({
    session_id: z.string().optional(),
    kpis: kpisSchema.optional(),
  })
  .catchall(jsonValueSchema);

const historyRecordSchema = historySummarySchema.extend({
  payload: predictionPayloadSchema,
  review: predictionFormSchema.nullable().optional(),
});

const historyListSchema = z.object({
  items: z.array(historySummarySchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

type V2HistoryRequest = z.infer<typeof v2RequestSchema>;


function mapRequest(request: V2HistoryRequest): PredictionRequest {
  const platformBudgets = {
    douyin: request.platform_budgets.douyin,
    xhs: request.platform_budgets.xhs,
    wechat_video: request.platform_budgets.wechat_video,
    bilibili: request.platform_budgets.bilibili,
    kuaishou: request.platform_budgets.kuaishou,
    tiktok: request.platform_budgets.tiktok,
    instagram: request.platform_budgets.instagram,
    youtube: request.platform_budgets.youtube,
  };
  const totalBudget = Object.values(platformBudgets).reduce(
    (total, budget) => total + budget,
    0,
  );
  const platformAlloc = Object.fromEntries(
    PLATFORM_IDS.filter((platform) => platformBudgets[platform] > 0).map(
      (platform) => [platform, platformBudgets[platform] / totalBudget],
    ),
  );

  return {
    creative: {
      caption: request.caption,
      duration_sec: request.duration_sec,
      visual_style: request.visual_style,
      music_mood: request.music_mood,
      has_celeb: request.has_celeb,
    },
    project_name: request.project_name,
    campaign_objective: request.campaign_objective,
    core_selling_points: request.core_selling_points,
    source: request.source,
    platform_budgets: platformBudgets,
    total_budget: totalBudget,
    platform_alloc: platformAlloc,
    audience_age_buckets: request.audience_age_bucket,
    audience_gender: request.audience_gender,
    audience_city_tiers: request.audience_city_tier,
    use_llm: request.use_llm,
    llm_calibrate: request.llm_calibrate,
    n_souls: request.n_souls,
    lifecycle_days: request.lifecycle_days,
    today: request.today,
    daypart: request.daypart,
    weather_temp_c: request.weather_temp_c,
    rainy: request.rainy,
    sentiment: request.sentiment,
    cross_platform_overlap: request.cross_platform_overlap,
    enable_crossplat: request.enable_crossplat,
    enable_discourse: request.enable_discourse,
    discourse_n_comments: request.discourse_n_comments,
    enable_brand_memory: request.enable_brand_memory,
    brand_memory_days: request.brand_memory_days,
    enable_recsys_rl: request.enable_recsys_rl,
    enable_groupchat: request.enable_groupchat,
    groupchat_n_agents: request.groupchat_n_agents,
    groupchat_n_rounds: request.groupchat_n_rounds,
    own_brand: request.own_brand || null,
    category: request.category || null,
    competitors: request.competitors,
    enable_kol_ilp: request.enable_kol_ilp,
    enable_search_elasticity: request.enable_search_elasticity,
    season: request.season,
    holiday_context: request.holiday_context,
    upcoming_holiday: request.upcoming_holiday,
    weather_desc: request.weather_desc,
    sentiment_reason: request.sentiment_reason,
    world_news_summary: request.world_news_summary,
    competitor_moves: request.competitor_moves,
    social_trends: request.social_trends,
    policy_regulation: request.policy_regulation,
  };
}

function mapSummary(
  value: z.infer<typeof historySummarySchema>,
): PredictionHistorySummary {
  return {
    id: value.id,
    createdAt: value.created_at * 1_000,
    elapsedMs: value.elapsed_ms,
    status: value.status,
    currentStep: value.current_step,
    errorMessage: value.error_message,
    request:
      "creative" in value.request ? value.request : mapRequest(value.request),
    kpis: value.kpis,
  };
}

export async function listPredictionHistory(): Promise<
  PredictionHistorySummary[]
> {
  if (isDemoMode()) return [];
  const response = await apiFetch(`${HISTORY_PATH}?limit=50&offset=0`);
  const parsed = historyListSchema.parse(await response.json());
  return parsed.items.map(mapSummary);
}

export async function getPredictionHistory(
  id: string,
): Promise<PredictionHistoryRecord | undefined> {
  if (!id || isDemoMode()) return undefined;
  const response = await apiFetch(`${HISTORY_PATH}/${encodeURIComponent(id)}`);
  const parsed = historyRecordSchema.parse(await response.json());
  return {
    ...mapSummary(parsed),
    payload: parsed.status === "completed" ? parsed.payload : null,
    review: parsed.review || undefined,
  };
}

export async function getLatestPrediction(): Promise<
  PredictionHistoryRecord | undefined
> {
  if (isDemoMode()) return undefined;
  const response = await apiFetch(`${HISTORY_PATH}?limit=1&offset=0`);
  const parsed = historyListSchema.parse(await response.json());
  const latest = parsed.items[0];
  return latest ? getPredictionHistory(latest.id) : undefined;
}

export async function deletePredictionHistory(id: string): Promise<void> {
  if (!id || isDemoMode()) return;
  await apiFetch(`${HISTORY_PATH}/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function clearPredictionHistory(): Promise<void> {
  if (isDemoMode()) return;
  await apiFetch(HISTORY_PATH, { method: "DELETE" });
}
import { isDemoMode } from "@/lib/api/demoMode";
